import { Card } from 'components/Card';
import { Icon, ICONS } from 'components/Icon';
import { Txt } from 'components/Txt';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { format, max, subDays } from 'date-fns';
import { ScheduledVisitMessageTemplate } from 'types/DTOs/BlockManagerDTOs';
import { CardRow } from 'components/CardRow';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { filterArrayEmpty } from 'lib/riskbase';

type EngageScheduledVisitTimelineProps = {
  templates: ScheduledVisitMessageTemplate[];
  earliestSelectedDate?: Date | null;
  publishedDate?: Date | null;
  loading: boolean;
};

export const EngageScheduledVisitTimeline: React.FC<
  EngageScheduledVisitTimelineProps
> = ({ templates, earliestSelectedDate, publishedDate, loading }) => {
  const [isOpen, setIsOpen] = React.useState<Record<string, boolean>>({});

  return (
    <>
      <Card label="Message Timeline" labelSize="large" margin={false}></Card>
      {loading && (
        <Card margin={false}>
          <CardRow contentStyle={{ paddingTop: 0, paddingBottom: 0 }}>
            <ActivityIndicator size={16} />
          </CardRow>
        </Card>
      )}
      {!loading &&
        templates.map((template) => {
          if (template.id === 'door_check_scheduled_visit_cancellation_email') {
            return null;
          }

          // The send date is the latest of the following:
          // - x days before earliestSelectedDate or
          // - if published, the published date, otherwise the current date
          const sendDate =
            earliestSelectedDate &&
            max(
              [
                subDays(earliestSelectedDate, template.sendDays),
                publishedDate || new Date(),
              ].filter(filterArrayEmpty),
            );

          const isPreviewOpen = isOpen[template.name] ?? false;

          return (
            <Card key={template.id} marginBottomQuarter>
              <CardRow
                icon={ICONS.ChevronRightLight}
                iconSize={16}
                iconStyle={{
                  transform: [{ rotate: isPreviewOpen ? '90deg' : '0deg' }],
                }}
                onPress={() => {
                  setIsOpen((prev) => ({
                    ...prev,
                    [template.name]: !(prev[template.name] ?? false),
                  }));
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Txt size="normal" weight="medium" style={{ flex: 1 }}>
                    {template.name}
                  </Txt>

                  {sendDate && (
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.paddingHorizontalHalf,
                      }}
                    >
                      <Icon
                        source={ICONS.ClockLight}
                        color={theme.colors.fieldValueText}
                        size={16}
                      />
                      <Txt
                        size="small"
                        weight="medium"
                        style={{ color: theme.colors.fieldValueText }}
                      >
                        {format(sendDate, UI_DATE_FORMAT_LONG)}
                      </Txt>
                    </View>
                  )}
                </View>
                <Txt style={{ marginTop: 2 }} size="xsmall">
                  {template.description}
                </Txt>
              </CardRow>
              {isPreviewOpen && (
                <CardRow>
                  <Txt weight="medium" size="small">
                    Subject: {template.subject}
                  </Txt>
                  <Txt size="small">{template.body}</Txt>
                </CardRow>
              )}
            </Card>
          );
        })}
    </>
  );
};
